import { useCallback, useSyncExternalStore } from "react";
import { registerDevtoolsFunction } from "./devtools/registry";
import { useParams, usePathname } from "./hooks";
import { findNearestNotFoundRoute, setNotFoundState } from "./notFoundState";
import { useContextKey } from "./router/Route";
import { getContextKey } from "./router/matchers";
import { router } from "./router/imperative-api";
import { preloadedLoaderData, preloadingLoader, routeNode } from "./router/router";
import { setSSRLoaderData, ssrLoaderData } from "./server/ssrLoaderData";
import { getClientMatchesSnapshot, subscribeToClientMatches, updateMatchLoaderData } from "./useMatches";
import { getLoaderPath } from "./utils/cleanUrl";
import { getURL } from "./getURL";
import { LOADER_JS_POSTFIX_UNCACHED } from "./constants";
import { dynamicImport } from "./utils/dynamicImport";
import { weakKey } from "./utils/weakKey";
import { useServerContext } from "./vite/one-server-only";

function _instanceof(left, right) {
	if (right != null && typeof Symbol !== "undefined" && right[Symbol.hasInstance]) return !!right[Symbol.hasInstance](left);
	else return left instanceof right;
}
var LOADER_TIMEOUT = process.env.ONE_LOADER_TIMEOUT_MS ? +process.env.ONE_LOADER_TIMEOUT_MS : 6e4;
var loaderTimingHistory = [];
var MAX_TIMING_HISTORY = 50;
var recordLoaderTiming = process.env.NODE_ENV === "development" ? function(entry) {
	loaderTimingHistory.unshift(entry);
	if (loaderTimingHistory.length > MAX_TIMING_HISTORY) loaderTimingHistory.pop();
} : void 0;
function getLoaderTimingHistory() {
	return loaderTimingHistory;
}
registerDevtoolsFunction("getLoaderTimingHistory", getLoaderTimingHistory);
registerDevtoolsFunction("recordLoaderTiming", recordLoaderTiming);
var loaderState = {};
var subscribers = /* @__PURE__ */ new Set();
var LOADER_STATE_MAX = 200;
var loaderStateKeys = [];
function setBoundedLoaderState(path, entry) {
	if (!(path in loaderState)) {
		loaderStateKeys.push(path);
		if (loaderStateKeys.length > LOADER_STATE_MAX) {
			var oldest = loaderStateKeys.shift();
			delete loaderState[oldest];
		}
	}
	loaderState[path] = entry;
}
function updateState(path, updates) {
	setBoundedLoaderState(path, {
		...loaderState[path],
		...updates
	});
	subscribers.forEach(function(callback) {
		callback();
	});
}
function subscribe(callback) {
	subscribers.add(callback);
	return function() {
		return subscribers.delete(callback);
	};
}
function getLoaderState(path, preloadedData) {
	if (!(path in loaderState)) setBoundedLoaderState(path, {
		data: preloadedData,
		error: void 0,
		promise: void 0,
		state: "idle",
		hasLoadedOnce: !!preloadedData
	});
	return loaderState[path];
}
async function refetchLoader(pathname) {
	var startTime = performance.now();
	updateState(pathname, {
		state: "loading",
		error: null
	});
	try {
		var _dynamicImport;
		var loaderJSUrl = getLoaderPath(pathname, true, `${Date.now()}`);
		var moduleLoadStart = performance.now();
		var module = await ((_dynamicImport = dynamicImport(loaderJSUrl)) === null || _dynamicImport === void 0 ? void 0 : _dynamicImport.catch(function() {
			return null;
		}));
		var moduleLoadTime = performance.now() - moduleLoadStart;
		if (!(module === null || module === void 0 ? void 0 : module.loader)) {
			updateState(pathname, {
				data: void 0,
				state: "idle",
				hasLoadedOnce: true
			});
			return;
		}
		var executionStart = performance.now();
		var result = await module.loader();
		var executionTime = performance.now() - executionStart;
		var totalTime = performance.now() - startTime;
		if (result === null || result === void 0 ? void 0 : result.__oneRedirect) {
			recordLoaderTiming === null || recordLoaderTiming === void 0 || recordLoaderTiming({
				path: pathname,
				startTime,
				moduleLoadTime,
				executionTime,
				totalTime,
				source: "refetch"
			});
			updateState(pathname, {
				data: void 0,
				state: "idle",
				hasLoadedOnce: true
			});
			router.replace(result.__oneRedirect);
			return;
		}
		if ((result === null || result === void 0 ? void 0 : result.__oneError) === 404) {
			recordLoaderTiming === null || recordLoaderTiming === void 0 || recordLoaderTiming({
				path: pathname,
				startTime,
				moduleLoadTime,
				executionTime,
				totalTime,
				source: "refetch"
			});
			var notFoundRoute = findNearestNotFoundRoute(pathname, routeNode);
			setNotFoundState({
				notFoundPath: result.__oneNotFoundPath || "/+not-found",
				notFoundRouteNode: notFoundRoute || void 0,
				originalPath: pathname
			});
			return;
		}
		updateState(pathname, {
			data: result,
			state: "idle",
			timestamp: Date.now(),
			hasLoadedOnce: true
		});
		var currentMatches = getClientMatchesSnapshot();
		var pageMatch = currentMatches[currentMatches.length - 1];
		var normalizedPathname = pathname.replace(/\/$/, "") || "/";
		var normalizedMatchPathname = ((pageMatch === null || pageMatch === void 0 ? void 0 : pageMatch.pathname) || "").replace(/\/$/, "") || "/";
		if (pageMatch && normalizedMatchPathname === normalizedPathname) updateMatchLoaderData(pageMatch.routeId, result);
		recordLoaderTiming === null || recordLoaderTiming === void 0 || recordLoaderTiming({
			path: pathname,
			startTime,
			moduleLoadTime,
			executionTime,
			totalTime,
			source: "refetch"
		});
	} catch (err) {
		var totalTime1 = performance.now() - startTime;
		updateState(pathname, {
			error: err,
			state: "idle"
		});
		recordLoaderTiming === null || recordLoaderTiming === void 0 || recordLoaderTiming({
			path: pathname,
			startTime,
			totalTime: totalTime1,
			error: _instanceof(err, Error) ? err.message : String(err),
			source: "refetch"
		});
		throw err;
	}
}
if (process.env.NODE_ENV === "development" && typeof window !== "undefined") window.__oneRefetchLoader = refetchLoader;
async function refetchMatchLoader(routeId, currentPath) {
	var _dynamicImport;
	var module = await ((_dynamicImport = dynamicImport(getLoaderPath(currentPath, true, `${Date.now()}`))) === null || _dynamicImport === void 0 ? void 0 : _dynamicImport.catch(function() {
		return null;
	}));
	if (!(module === null || module === void 0 ? void 0 : module.loader)) return;
	var result = await module.loader();
	if ((result === null || result === void 0 ? void 0 : result.__oneRedirect) || (result === null || result === void 0 ? void 0 : result.__oneError)) return;
	updateMatchLoaderData(routeId, result);
}
function useLoaderState(loader) {
	var { loaderProps: loaderPropsFromServerContext, loaderData: loaderDataFromServerContext } = useServerContext() || {};
	var params = useParams();
	var pathname = usePathname();
	var currentPath = pathname.replace(/\/index$/, "").replace(/\/$/, "") || "/";
	if (typeof window === "undefined") {
		if (loader) {
			if (ssrLoaderData.has(loader)) return {
				data: ssrLoaderData.get(loader),
				refetch: async function() {},
				state: "idle"
			};
			var serverContext = useServerContext();
			if (serverContext === null || serverContext === void 0 ? void 0 : serverContext.matches) {
				var contextKey = useContextKey();
				var match = serverContext.matches.find(function(m) {
					return getContextKey(m.routeId) === contextKey;
				});
				if (match && match.loaderData !== void 0) return {
					data: match.loaderData,
					refetch: async function() {},
					state: "idle"
				};
			}
			return {
				data: useAsyncFn(loader, loaderPropsFromServerContext || {
					path: pathname,
					params
				}),
				refetch: async function() {},
				state: "idle"
			};
		}
		if (loaderDataFromServerContext !== void 0) return {
			data: loaderDataFromServerContext,
			refetch: async function() {},
			state: "idle"
		};
	}
	var matchRouteId = loader ? (function() {
		var result = loader();
		return typeof result === "string" && result.startsWith("./") ? result : null;
	})() : null;
	var clientMatches = useSyncExternalStore(subscribeToClientMatches, getClientMatchesSnapshot, getClientMatchesSnapshot);
	var preloadedData = (loaderPropsFromServerContext === null || loaderPropsFromServerContext === void 0 ? void 0 : loaderPropsFromServerContext.path) === currentPath ? loaderDataFromServerContext : void 0;
	var loaderStateEntry = useSyncExternalStore(subscribe, function() {
		return getLoaderState(currentPath, preloadedData);
	}, function() {
		return getLoaderState(currentPath, preloadedData);
	});
	var refetch = useCallback(function() {
		return refetchLoader(currentPath);
	}, [currentPath]);
	if (matchRouteId) {
		var _clientMatches_;
		var match1 = clientMatches.find(function(m) {
			return m.routeId === matchRouteId;
		});
		var isPageMatch = clientMatches.length > 0 && ((_clientMatches_ = clientMatches[clientMatches.length - 1]) === null || _clientMatches_ === void 0 ? void 0 : _clientMatches_.routeId) === matchRouteId;
		var matchPathNormalized = ((match1 === null || match1 === void 0 ? void 0 : match1.pathname) || "").replace(/\/$/, "") || "/";
		var matchPathFresh = !isPageMatch || matchPathNormalized === currentPath;
		if (match1 && match1.loaderData != null && matchPathFresh) return {
			data: match1.loaderData,
			refetch: async function() {
				await refetchLoader(currentPath);
				var fresh = loaderState[currentPath];
				if ((fresh === null || fresh === void 0 ? void 0 : fresh.data) != null) updateMatchLoaderData(matchRouteId, fresh.data);
			},
			state: loaderStateEntry.state
		};
	}
	if (!loader) return {
		refetch,
		state: loaderStateEntry.state
	};
	if (!loaderStateEntry.data && !loaderStateEntry.promise && !loaderStateEntry.hasLoadedOnce) {
		var resolvedPreloadData = preloadedLoaderData[currentPath];
		if (resolvedPreloadData != null) {
			delete preloadedLoaderData[currentPath];
			delete preloadingLoader[currentPath];
			loaderStateEntry.data = resolvedPreloadData;
			loaderStateEntry.hasLoadedOnce = true;
		} else if (preloadingLoader[currentPath]) loaderStateEntry.promise = preloadingLoader[currentPath].then(function(val) {
			delete preloadingLoader[currentPath];
			delete preloadedLoaderData[currentPath];
			if (val != null) updateState(currentPath, {
				data: val,
				hasLoadedOnce: true,
				promise: void 0
			});
			else updateState(currentPath, { promise: void 0 });
		}).catch(function(err) {
			console.error(`Error running loader()`, err);
			delete preloadingLoader[currentPath];
			updateState(currentPath, {
				error: err,
				promise: void 0
			});
		});
		else {
			var loadData = async function() {
				var startTime = performance.now();
				try {
					var nativeLoaderJSUrl;
					if (process.env.NODE_ENV === "development") nativeLoaderJSUrl = `${getLoaderPath(currentPath, true)}?platform=native`;
					else nativeLoaderJSUrl = `${getURL()}/assets/${currentPath.slice(1).replace(/\/$/, "").replaceAll("_", "__").replaceAll("/", "_")}${LOADER_JS_POSTFIX_UNCACHED.replace(".js", ".native.js")}`;
					try {
						var moduleLoadStart = performance.now();
						var controller = new AbortController();
						var timeoutId = setTimeout(function() {
							return controller.abort();
						}, LOADER_TIMEOUT);
						var loaderJsCode;
						try {
							var loaderJsCodeResp = await fetch(nativeLoaderJSUrl, { signal: controller.signal });
							if (!loaderJsCodeResp.ok) throw new Error(`Response not ok: ${loaderJsCodeResp.status}`);
							loaderJsCode = await loaderJsCodeResp.text();
						} finally {
							clearTimeout(timeoutId);
						}
						var result = {};
						new Function("exports", loaderJsCode)(result);
						var moduleLoadTime = performance.now() - moduleLoadStart;
						if (typeof result.loader !== "function") throw new Error("Loader code isn't exporting a `loader` function");
						var executionStart = performance.now();
						var data = await result.loader();
						var executionTime = performance.now() - executionStart;
						var totalTime = performance.now() - startTime;
						if (data === null || data === void 0 ? void 0 : data.__oneRedirect) {
							recordLoaderTiming === null || recordLoaderTiming === void 0 || recordLoaderTiming({
								path: currentPath,
								startTime,
								moduleLoadTime,
								executionTime,
								totalTime,
								source: "initial"
							});
							updateState(currentPath, {
								data: void 0,
								hasLoadedOnce: true,
								promise: void 0
							});
							router.replace(data.__oneRedirect);
							return;
						}
						if ((data === null || data === void 0 ? void 0 : data.__oneError) === 404) {
							recordLoaderTiming === null || recordLoaderTiming === void 0 || recordLoaderTiming({
								path: currentPath,
								startTime,
								moduleLoadTime,
								executionTime,
								totalTime,
								source: "initial"
							});
							var notFoundRoute = findNearestNotFoundRoute(currentPath, routeNode);
							setNotFoundState({
								notFoundPath: data.__oneNotFoundPath || "/+not-found",
								notFoundRouteNode: notFoundRoute || void 0,
								originalPath: currentPath
							});
							return;
						}
						updateState(currentPath, {
							data,
							hasLoadedOnce: true,
							promise: void 0
						});
						recordLoaderTiming === null || recordLoaderTiming === void 0 || recordLoaderTiming({
							path: currentPath,
							startTime,
							moduleLoadTime,
							executionTime,
							totalTime,
							source: "initial"
						});
						return;
					} catch (e) {
						console.error(`[one] native loader error for ${currentPath}:`, _instanceof(e, Error) ? e.message : e, `url: ${nativeLoaderJSUrl}`);
						var totalTime1 = performance.now() - startTime;
						updateState(currentPath, {
							data: {},
							promise: void 0
						});
						recordLoaderTiming === null || recordLoaderTiming === void 0 || recordLoaderTiming({
							path: currentPath,
							startTime,
							totalTime: totalTime1,
							error: _instanceof(e, Error) ? e.message : String(e),
							source: "initial"
						});
						return;
					}
				} catch (err) {
					var totalTime3 = performance.now() - startTime;
					updateState(currentPath, {
						error: err,
						promise: void 0
					});
					recordLoaderTiming === null || recordLoaderTiming === void 0 || recordLoaderTiming({
						path: currentPath,
						startTime,
						totalTime: totalTime3,
						error: _instanceof(err, Error) ? err.message : String(err),
						source: "initial"
					});
				}
			};
			loaderStateEntry.promise = loadData();
		}
	}
	if (loaderStateEntry.error && !loaderStateEntry.hasLoadedOnce) throw loaderStateEntry.error;
	if (loaderStateEntry.data === void 0 && loaderStateEntry.promise && !loaderStateEntry.hasLoadedOnce) throw loaderStateEntry.promise;
	return {
		data: loaderStateEntry.data,
		refetch,
		state: loaderStateEntry.state
	};
}
function useLoader(loader) {
	var { data } = useLoaderState(loader);
	return data;
}
var results = /* @__PURE__ */ new Map();
var started = /* @__PURE__ */ new Map();
var USE_ASYNC_FN_CACHE_MAX = 100;
function setBoundedResults(key, value) {
	if (results.size >= USE_ASYNC_FN_CACHE_MAX && !results.has(key)) {
		var firstKey = results.keys().next().value;
		if (firstKey !== void 0) {
			results.delete(firstKey);
			started.delete(firstKey);
		}
	}
	results.set(key, value);
}
function resetLoaderState() {
	results.clear();
	started.clear();
}
function useAsyncFn(val, props) {
	var key = (val ? weakKey(val) : "") + JSON.stringify(props);
	if (val) {
		if (!started.get(key)) {
			started.set(key, true);
			var next = val(props);
			if (_instanceof(next, Promise)) next = next.then(function(final) {
				setBoundedResults(key, final);
			}).catch(function(err) {
				console.error(`Error running loader()`, err);
				setBoundedResults(key, void 0);
			});
			setBoundedResults(key, next);
		}
	}
	var current = results.get(key);
	if (_instanceof(current, Promise)) throw current;
	return current;
}

export { getLoaderTimingHistory, refetchLoader, refetchMatchLoader, resetLoaderState, setSSRLoaderData, useLoader, useLoaderState };